import { MuiButton } from '@/components/button/MuiButton';
import { MuiChip } from '@/components/chip/MuiChip';
import { HpUrlIcon } from '@/features/icon/HpUrlIcon';
import { PhoneIcon } from '@/features/icon/PhoneIcon';
import { CanRequestLabel } from '@/features/label/CanRequestLabel';
import { DxStationLabel } from '@/features/label/DxStationLabel';
import { PartnershipsSummary } from '@/features/partnership/components/PartnershipsSummary';
import { ServiceList } from '@/features/station/ServiceList';
import {
  api_portalOfficeListing_filter,
  ibow2_proto_PortalOfficeListing,
} from '@/init/swagger';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { useLogic } from './logic';
import EventIcon from '@mui/icons-material/Event';
import { OnlyMemberComponent } from '@/features/public/OnlyMemberComponent';

type Props = {
  listing: ibow2_proto_PortalOfficeListing;
  searchFilters?: api_portalOfficeListing_filter[];
};

export function PublicHomeStationListingCard({
  listing,
  searchFilters = [],
}: Props) {
  const {
    cardHref,
    thumbnailInfo,
    portalOfficeInfo,
    publishedOnStr,
    updatedOnStr,
    isDxStation,
    isCarelogood,
    hasServiceCategoryItems,
    serviceCategoryItems,
    onClickRequest,
  } = useLogic({
    listing,
    searchFilters,
  });

  return (
    <CardActionArea href={cardHref} target="_blank">
      <Card
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
          boxShadow: `0px 0px 2px 0px rgba(0, 0, 0, 0.10), 0px 4px 8px 0px rgba(0, 0, 0, 0.10)`,
          borderRadius: 2,
          padding: 2,
          gap: {
            xs: 2,
            md: 3,
          },
          backgroundColor: 'white',
          position: 'relative',
          alignItems: 'flex-start',
          '&:hover': {
            '& .highlight span': {
              textDecoration: 'underline',
              textDecorationColor: '#3ec9be',
              color: '#3ec9be',
            },
            '& .MuiCardMedia-img': {
              border: 'solid 2px #3ec9be',
            },
          },
        }}
      >
        <Box
          sx={{
            width: {
              xs: '100%',
              md: 320,
            },
            maxWidth: {
              xs: '100%',
              md: '40%',
            },
            aspectRatio: {
              xs: '1 / 1',
              md: '16 / 9',
            },
            height: {
              xs: 263,
              md: 220,
            },
            borderRadius: 1,
            margin: {
              xs: 'auto',
              md: '16px auto',
            },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <img
            src={thumbnailInfo.image}
            alt={thumbnailInfo.alt}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: 'inherit',
              objectFit: 'cover',
            }}
          />
        </Box>
        <CardContent
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            py: 1,
            gap: 2,
            ':last-child': { pb: 1 },
            flex: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                <Box>
                  <MuiChip
                    label={`看護・介護サービス`}
                    sx={{
                      fontWeight: 700,
                      backgroundColor: '#94F2EA',
                      height: '24px',
                      fontSize: '14px',
                    }}
                  />
                </Box>
                <div className="highlight">
                  <Typography
                    variant="JP/Heading/s/Bold/Tight"
                    sx={{ verticalAlign: 'sub' }}
                  >
                    {listing.portal_office?.name}
                  </Typography>
                </div>
              </Box>
            </Box>

            {(isDxStation || isCarelogood) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1,
                }}
              >
                {isDxStation && <DxStationLabel />}
                {isCarelogood && <CanRequestLabel />}
              </Box>
            )}

            <PartnershipsSummary
              portalOfficeId={Number(listing.portal_office_id)}
            />

            <Box
              sx={{
                order: {
                  xs: 1,
                  md: 2,
                },
                '@container hospital-listing-container (width < 600px)': {
                  order: 1,
                },
              }}
            >
              <Typography color="#595960" variant="JP/Body/m/Regular/Normal">
                {portalOfficeInfo.postCode
                  ? `${portalOfficeInfo.postCode} ${portalOfficeInfo.address}`
                  : portalOfficeInfo.address}
              </Typography>

              {portalOfficeInfo.tel && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <PhoneIcon color="default" width={16} height={16} />
                  <Typography
                    color="#595960"
                    variant="JP/Body/m/Regular/Normal"
                  >
                    {portalOfficeInfo.tel}
                  </Typography>
                </Box>
              )}

              {hasServiceCategoryItems && (
                <Box sx={{ mt: 1 }}>
                  <ServiceList
                    services={serviceCategoryItems || []}
                    searchFilters={searchFilters}
                  />
                </Box>
              )}
            </Box>

            {isCarelogood && (
              <Stack
                alignItems="center"
                direction="row"
                gap={3}
                sx={{ order: 3 }}
              >
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <EventIcon sx={{ color: '#C4C4C4', marginTop: '4px' }} />
                  <Typography
                    variant="JP/Body/m/Regular/Normal"
                    color="#595960"
                  >
                    営業時間
                  </Typography>
                </Box>
                <OnlyMemberComponent />
              </Stack>
            )}

            {listing.official_hp_url && (
              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1,
                  order: 4,
                }}
              >
                <HpUrlIcon
                  color="default"
                  width={18}
                  height={18}
                  sxProps={{
                    marginTop: '2px',
                  }}
                />
                <Typography variant="JP/Body/m/Regular/Normal" color="#595960">
                  {listing.official_hp_url}
                </Typography>
              </Stack>
            )}
          </Box>

          {isCarelogood && (
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                color: 'primary.main',
                justifyContent: {
                  xs: 'center',
                  md: 'flex-start',
                },
                '@container hospital-listing-container (width < 600px)': {
                  justifyContent: 'center',
                },
              }}
            >
              <MuiButton variant="text" onClick={onClickRequest}>
                <Typography variant="JP/Body/s/Medium/None">
                  看護・介護サービスお問い合わせへ
                </Typography>
                <NavigateNextIcon fontSize="small" />
              </MuiButton>
            </Stack>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="JP/Body/m/Medium/Tight" color="#C4C4C4">
              掲載日：{publishedOnStr}
            </Typography>
            <Typography variant="JP/Body/m/Medium/Tight" color="#C4C4C4">
              最終更新日：{updatedOnStr}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </CardActionArea>
  );
}
